import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../lib/AuthContext";
import { BookmarkSimple, CircleNotch } from "@phosphor-icons/react";
import { Button } from "./ui/button";

const SaveProviderBtn = ({ npi, providerName, entityType, specialty, state }) => {
  const { user, isAuthenticated, loading: authLoading, login, authFetch } = useAuth();
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  const checkStatus = useCallback(async () => {
    if (!user || !npi) { setChecking(false); return; }
    try {
      const res = await authFetch(`${window.location.origin}/api/dashboard/saved/check/${npi}`);
      if (res.ok) { const data = await res.json(); setSaved(data.saved); }
    } catch { /* ignore */ }
    finally { setChecking(false); }
  }, [user, npi, authFetch]);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  if (authLoading) return null;

  if (!isAuthenticated) {
    return (
      <Button onClick={() => login({ appState: { returnTo: window.location.pathname } })}
        variant="outline" size="sm" className="gap-2 rounded-lg text-sm border-slate-300" data-testid="save-provider-login-btn">
        <BookmarkSimple className="w-4 h-4" /> Save
      </Button>
    );
  }

  if (!user) {
    return (
      <Button variant="outline" size="sm" className="gap-2 rounded-lg text-sm border-slate-300" disabled>
        <CircleNotch className="w-4 h-4 animate-spin" /> Save
      </Button>
    );
  }

  if (saved) {
    return (
      <Button onClick={async () => {
        setLoading(true);
        try { await authFetch(`${window.location.origin}/api/dashboard/saved/remove/${npi}`, { method: "DELETE" }); setSaved(false); }
        catch { /* ignore */ } finally { setLoading(false); }
      }} variant="outline" size="sm" className="gap-2 rounded-lg text-sm border-teal/30 bg-teal/10 text-teal" data-testid="save-provider-saved-btn" disabled={loading}>
        {loading ? <CircleNotch className="w-4 h-4 animate-spin" /> : <BookmarkSimple className="w-4 h-4" weight="fill" />} Saved
      </Button>
    );
  }

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${window.location.origin}/api/dashboard/saved/add`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ npi, provider_name: providerName, entity_type: entityType, specialty, state }),
      });
      if (res.ok) setSaved(true);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  return (
    <Button onClick={handleSave} disabled={loading || checking}
      variant="outline" size="sm" className="gap-2 rounded-lg text-sm border-slate-300 hover:border-teal hover:text-teal" data-testid="save-provider-btn">
      {loading ? <CircleNotch className="w-4 h-4 animate-spin" /> : <BookmarkSimple className="w-4 h-4" />} Save
    </Button>
  );
};

export default SaveProviderBtn;
